import React, { useEffect, useState } from 'react'
import { NavLink, Redirect } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import { useCookies } from 'react-cookie'
import { CircularProgress } from '@material-ui/core'
import { SSO_AUTH } from '../queriesAndMutations'
import LoginRegisterHeader from '../components/HeaderLoginRegister'
import LoginRegisterHeaderRwd from '../components/HeaderLoginRegisterRwd'
import LoginRegisterLogo from '../components/LogoLoginRegister'
import { usePublicSettings } from '../myHooks/useSettings'
import LineInformation from '../components/InformationLine'
import { useTranslation } from 'react-i18next'
import { resolveInvesthubSsoUrl } from '../utils/investhubSsoUrl'

const SsoAuth = () => {
  const [, setCookie] = useCookies(['jwt'])
  // Capture SSO handoff once, then strip query so ?token= never lingers in
  // address bar / history / Referer (SSO review a26ebd88 high finding).
  const [{ token, legacySsoToken }] = useState(() => {
    const query = new URLSearchParams(window.location.search)
    const next = {
      token: query.get('token'),
      legacySsoToken: query.get('sso_token'),
    }
    if (next.token || next.legacySsoToken) {
      const clean = `${window.location.pathname}${window.location.hash || ''}`
      window.history.replaceState(window.history.state, '', clean)
    }
    return next
  })
  const { data: { publicSettings = {} } = {} } = usePublicSettings()
  const { t } = useTranslation()
  const [ssoAuth, { data, loading, error }] = useMutation(SSO_AUTH)
  const [started, setStarted] = useState(false)

  const investhubUrl = resolveInvesthubSsoUrl(publicSettings)
  const ssoRequestUrl = `${investhubUrl}/sso/request?redirect=${encodeURIComponent(`${window.location.origin}/sso/auth`)}&company_id=${publicSettings.investhub_company_id || ''}&company_type=investor`

  useEffect(() => {
    let meta = document.querySelector('meta[name="referrer"]')
    const created = !meta
    const previous = meta ? meta.getAttribute('content') : null
    if (!meta) {
      meta = document.createElement('meta')
      meta.setAttribute('name', 'referrer')
      document.head.appendChild(meta)
    }
    meta.setAttribute('content', 'no-referrer')
    return () => {
      if (!meta) return
      if (created) {
        meta.remove()
      } else if (previous == null) {
        meta.removeAttribute('content')
      } else {
        meta.setAttribute('content', previous)
      }
    }
  }, [])

  useEffect(() => {
    if (!token || started || legacySsoToken) {
      return
    }
    setStarted(true)
    ssoAuth({ variables: { input: { token } } }).catch(() => {})
  }, [token, started, ssoAuth, legacySsoToken])

  const noKycApplication = Boolean(data && data.ssoAuth && data.ssoAuth.no_kyc_application)
  const companyDisplayName = (
    (publicSettings.company && publicSettings.company.name)
    || publicSettings.token_name
    || ''
  ).trim()

  if (data && data.ssoAuth && data.ssoAuth.accessToken && !noKycApplication) {
    setCookie('jwt', data.ssoAuth.accessToken, { path: '/' })
    localStorage.setItem('jwt', data.ssoAuth.accessToken)
    return <Redirect to="/" />
  }

  return (
    <>
      <LoginRegisterHeader>
        <a href={investhubUrl}>{ t('Back to Investhub') }</a>
      </LoginRegisterHeader>

      <LoginRegisterHeaderRwd>
        <div className="actions__option actions__option_normal d__block">
          <a href={investhubUrl}>{ t('Back to Investhub') }</a>
        </div>
      </LoginRegisterHeaderRwd>

      <div className="entry entry_loginregister">
        <div className="entry__wrapper entry__wrapper-kyc">
          <LoginRegisterLogo />

          <div className="h2 entry__title">
            { t('Sign In') }
          </div>
          <div className="entry__text">
            { t('Continue with Investhub') }
          </div>

          <div className="entry__fieldset">
            {legacySsoToken && (
              <LineInformation
                informationColor="box__information-red"
                icon="close"
                text={
                  <>
                    {t('Please sign in again via Investhub')}
                    {' '}
                    <a href={ssoRequestUrl}>{t('Continue with Investhub')}</a>
                  </>
                }
              />
            )}

            {!token && !legacySsoToken && (
              <LineInformation
                informationColor="box__information-red"
                icon="close"
                text={
                  <>
                    {t('SSO sign-in failed')}
                    {' '}
                    <NavLink to="/login">{t('Back to login page')}</NavLink>
                  </>
                }
              />
            )}

            {token && !legacySsoToken && loading && (
              <>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress />
                </div>
                <div className="entry__text">
                  { t('Verifying your Investhub sign-in…') }
                </div>
              </>
            )}

            {token && !legacySsoToken && !loading && noKycApplication && (
              <LineInformation
                informationColor="box__information-red"
                icon="close"
                text={
                  <>
                    {t('This email has no application on this company')}
                    {companyDisplayName ? (
                      <>
                        {' '}
                        ({companyDisplayName})
                      </>
                    ) : null}
                    {' '}
                    <NavLink to="/login">{t('Back to login page')}</NavLink>
                  </>
                }
              />
            )}

            {token && !legacySsoToken && !loading && error && (
              <LineInformation
                informationColor="box__information-red"
                icon="close"
                text={
                  <>
                    {t('SSO sign-in failed')}
                    {': '}
                    {error.message}
                    {' '}
                    <a href={investhubUrl}>{t('Back to Investhub')}</a>
                  </>
                }
              />
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default SsoAuth
